"use client";

import Link from "next/link";
import { useRouter } from "next/router";
import { FunctionComponent, MouseEvent, useMemo } from "react";
import { isExternalLink } from "@components/common/utils/helper";

import { useRouter as useRealclixxRouter } from "../hooks/router";

export interface ActiveLinkProps {
    /**
     * Children of the link.
     */
    children: React.ReactNode;

    /**
     * The classname(s) which will be applied when the link is active.
     */
    activeClassName?: string;

    /**
     * A callback function which will be fired on click to an anchor link.
     */
    handleAnchorClick?: () => void;

    /**
     * A callback function which will be fired onMouseEnter.
     */
    onMouseEnter?: (event: MouseEvent<HTMLElement>) => void;

    /**
     * A callback function which will be fired onMouseLeave.
     */
    onMouseLeave?: (event: MouseEvent<HTMLElement>) => void;

    /**
     * URL of the link.
     */
    href: string;

    /**
     * A callback function which will be fired on click.
     */
    onClick?: () => void;

    /**
     * When a link scroll to an anchor, you can define the delay between click and hash change.
     */
    delayAnchor?: number;

    /**
     * Class name of the link.
     */
    className?: string;
}

export const ActiveLink: FunctionComponent<ActiveLinkProps> = ({
    children,
    href,
    delayAnchor,
    handleAnchorClick,
    activeClassName,
    onMouseEnter,
    onMouseLeave,
    onClick,
    className,
    ...restProps
}) => {
    const router = useRouter();
    const { pathname } = router;
    const { anchorScroll } = useRealclixxRouter();

    const computedClassName = useMemo(() => {
        const classes = [className];
        if (pathname === "/" && href === "/") {
            classes.push(activeClassName);
        } else if (pathname !== "/" && href !== "/" && pathname.startsWith(href)) {
            classes.push(activeClassName);
        }

        return classes.join(" ").trim();
    }, [pathname, href, activeClassName, className]);

    return (
        <>
            {href.indexOf("#") > -1 ? (
                <>
                    {isExternalLink(href) || onClick ? (
                        <Link href={href} onClick={onClick} {...restProps}>
                            <a className={computedClassName} onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave}>
                                {children}
                            </a>
                        </Link>
                    ) : (
                        <Link
                            href={href}
                            {...restProps}
                            onClick={(e) => {
                                e.preventDefault();
                                handleAnchorClick?.();
                                anchorScroll(href, "smooth", true, delayAnchor);
                            }}
                        >
                            <a className={computedClassName} onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave}>
                                {children}
                            </a>
                        </Link>
                    )}
                </>
            ) : (
                <Link href={href} onClick={onClick} {...restProps}>
                    <a className={computedClassName} onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave}>
                        {children}
                    </a>
                </Link>
            )}
        </>
    );
};
